import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { format, addWeeks, subWeeks } from "date-fns";
import {
  Banknote,
  CalendarRange,
  Download,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import { AppModalShell, AppModalFooter } from "../components/app-ui.js";
import { Button } from "../components/legacy-ui.js";
import MilkProviderPaymentForm from "./providers/MilkProviderPaymentForm.js";
import WeeklyMilkSheet from "./providers/WeeklyMilkSheet.js";
import WeekPricesDialog from "./providers/WeekPricesDialog.js";
import QuickDayMilkDialog from "./providers/QuickDayMilkDialog.js";
import MilkWeekResumenTable, {
  aggregateWeekResumen,
} from "./providers/MilkWeekResumenTable.js";
import toast from "react-hot-toast";
import { companyPageTitle } from "../utils/company.js";
import {
  getProvider,
  createProvider,
  updateProvider,
  deleteProvider,
  getMilkDeliverySummary,
  upsertMilkDelivery,
  upsertMilkDayTrips,
  upsertMilkWeekPrice,
  deleteMilkProviderPayment,
  getBcvRate,
  pushAdminNotification,
} from "../utils/api.js";
import { exportProviderMilkTemplate, buildMilkReceiptModel } from "../utils/exportMilkTemplate.js";
import { LoadingState, SkeletonRows } from "../components/motion.js";
import { exportMilkWeekExcel } from "../utils/exportMilkWeekExcel.js";
import {
  milkPayStatus,
  providerDiscountLiters,
  providerGrossLiters,
} from "../utils/milkUtils.js";
import MilkReceiptSheet from "../components/MilkReceiptSheet.js";
import NotifyDayDialog from "../components/NotifyDayDialog.js";
import { shareOrDownloadInvoiceImage } from "../utils/invoiceShareImage.js";

const fmtMoney = (value) =>
  `$${Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtLiters = (value) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const emptyProviderForm = () => ({
  name: "",
  documentId: "",
  phone: "",
  isActive: true,
});

const dateKey = (value) => String(value || "").slice(0, 10);

const fmtDay = (value) => {
  const key = dateKey(value);
  if (key.length < 10) return "";
  const [, month, day] = key.split("-");
  return `${day}/${month}`;
};

const weekNavLabel = (weekly) => {
  if (!weekly) return "Cargando semana…";
  const days = weekly.days || [];
  const start = days[0]?.date || weekly.startOfWeek;
  const end = days[6]?.date || weekly.endOfWeek;
  const year = dateKey(end).slice(0, 4);
  return `Jueves ${fmtDay(start)} — Miércoles ${fmtDay(end)} ${year}`;
};

const ProvidersModule = ({ user } = {}) => {
  const hidePrices = user?.role === "quesero";
  const [summary, setSummary] = useState(null);
  const [weekDate, setWeekDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");

  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [providerForm, setProviderForm] = useState(emptyProviderForm);

  const [weekOpen, setWeekOpen] = useState(false);
  const [pricesOpen, setPricesOpen] = useState(false);
  const [dayMilkOpen, setDayMilkOpen] = useState(false);
  const [dayMilkPending, setDayMilkPending] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [weekSaving, setWeekSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [weekExporting, setWeekExporting] = useState(false);
  const [exportFormatOpen, setExportFormatOpen] = useState(false);
  const [receiptCapture, setReceiptCapture] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [notifying, setNotifying] = useState(false);
  const [deletingProviderId, setDeletingProviderId] = useState("");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const weekSheetRef = useRef(null);
  const searchRef = useRef(null);
  const receiptSheetRef = useRef(null);
  const actionMenuRef = useRef(null);

  useEffect(() => {
    document.title = companyPageTitle("Proveedores de leche");
  }, []);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const sumRes = await getMilkDeliverySummary({
        closeDay: 3,
        date: format(weekDate, "yyyy-MM-dd"),
      });
      if (sumRes.data.success) setSummary(sumRes.data.data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [weekDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onKey = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "f") return;
      if (weekOpen || showPaymentModal || showProviderModal || pricesOpen || dayMilkOpen) return;
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [weekOpen, showPaymentModal, showProviderModal, pricesOpen, dayMilkOpen]);

  const rows = useMemo(() => summary?.weekly?.providerTotals || [], [summary]);
  const weekly = summary?.weekly;
  const weekResumen = useMemo(
    () => aggregateWeekResumen(weekly?.days || [], rows),
    [weekly, rows],
  );

  useEffect(() => {
    if (!dayMilkPending || loading || !weekly?.days?.length) return;
    const today = format(new Date(), "yyyy-MM-dd");
    const inWeek = weekly.days.some((day) => dateKey(day.date) === today);
    if (!inWeek) return;
    setDayMilkPending(false);
    setDayMilkOpen(true);
  }, [dayMilkPending, loading, weekly]);
  const selectedRow = useMemo(
    () => rows.find((p) => p.id === selectedId) || null,
    [rows, selectedId],
  );
  const visibleRows = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((p) => String(p.name || "").toLowerCase().includes(term));
  }, [rows, searchQuery]);

  useEffect(() => {
    if (selectedId && !rows.some((p) => p.id === selectedId)) {
      setSelectedId("");
    }
  }, [rows, selectedId]);

  const weekPayments = useMemo(() => {
    if (!selectedRow || !summary?.weekly?.payments) return [];
    return summary.weekly.payments.filter((pay) => {
      const pid = pay.provider?._id || pay.provider;
      return String(pid) === String(selectedRow.id);
    });
  }, [selectedRow, summary]);

  const handleSaveWeek = useCallback(async ({ cells = [], prices = {}, trips = [] }) => {
    if (!selectedRow || !summary?.weekly) {
      throw new Error("No hay proveedor o semana seleccionada.");
    }
    await Promise.all(
      cells.map((cell) =>
        upsertMilkDelivery({
          provider: selectedRow.id,
          date: `${cell.date}T12:00:00`,
          milkType: cell.milkType,
          liters: cell.liters,
          discount: cell.discount || 0,
          discountScope: cell.discountScope || "general",
        }),
      ),
    );
    for (const item of trips) {
      await upsertMilkDayTrips({
        provider: selectedRow.id,
        date: `${item.date}T12:00:00`,
        trips: item.trips || [],
      });
    }
    if (Object.keys(prices).length > 0) {
      await upsertMilkWeekPrice({
        provider: selectedRow.id,
        weekStart: summary.weekly.startOfWeek,
        weekEnd: summary.weekly.endOfWeek,
        ...prices,
      });
    }
    await fetchData({ silent: true });
  }, [selectedRow, summary, fetchData]);

  const handleCreateProvider = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        const id = editingProvider._id || editingProvider.id;
        await updateProvider(id, providerForm);
        toast.success("Proveedor actualizado", { position: "top-center" });
      } else {
        await createProvider(providerForm);
        toast.success("Proveedor creado", { position: "top-center" });
      }
      setShowProviderModal(false);
      setEditingProvider(null);
      setProviderForm(emptyProviderForm());
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "No se pudo guardar el proveedor.", {
        position: "top-center",
      });
    }
  };

  const handleEdit = async (row) => {
    if (!row?.id) return;
    setSelectedId(row.id);
    try {
      const res = await getProvider(row.id);
      const provider = res.data?.data || row;
      setEditingProvider(provider);
      setProviderForm({
        name: provider.name || "",
        documentId: provider.documentId || "",
        phone: provider.phone || "",
        isActive: provider.isActive !== false,
      });
      setShowProviderModal(true);
    } catch (err) {
      console.error(err);
      setEditingProvider(row);
      setProviderForm({
        name: row.name || "",
        documentId: "",
        phone: "",
        isActive: true,
      });
      setShowProviderModal(true);
    }
  };

  const openWeek = (row) => {
    if (!row?.id) return;
    setSelectedId(row.id);
    setWeekOpen(true);
  };

  const openPayment = (row) => {
    if (!row?.id) return;
    setSelectedId(row.id);
    setShowPaymentModal(true);
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    const name = row.name || "este proveedor";
    const confirmed = window.confirm(
      `¿Eliminar a ${name}? También se borrarán sus entregas y abonos de leche. Esta acción no se puede deshacer.`,
    );
    if (!confirmed) return;
    setDeletingProviderId(row.id);
    try {
      await deleteProvider(row.id);
      toast.success("Proveedor eliminado", { position: "top-center" });
      setSelectedId("");
      setWeekOpen(false);
      setShowPaymentModal(false);
      await fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "No se pudo eliminar el proveedor.", {
        position: "top-center",
      });
    } finally {
      setDeletingProviderId("");
    }
  };

  const handleExport = (row) => {
    const target = row?.id ? row : selectedRow;
    if (!target) {
      toast.error("Selecciona un proveedor", { position: "top-center" });
      return;
    }
    setSelectedId(target.id);
    setExportFormatOpen(true);
  };

  const notifyDays = useMemo(() => {
    const days = weekly?.days || [];
    return days.map((day) => {
      const vaca = Number(weekResumen.dailyVaca?.[day.date]) || 0;
      const bufalo = Number(weekResumen.dailyBufalo?.[day.date]) || 0;
      const total = vaca + bufalo;
      const enabled = total > 0.009;
      return {
        date: day.date,
        label: `${day.label} ${fmtDay(day.date)}`,
        enabled,
        meta: enabled
          ? `${fmtLiters(total)} L`
          : "Sin leche",
      };
    });
  }, [weekly, weekResumen]);

  const canNotifyMilk = notifyDays.some((day) => day.enabled);

  const handleNotifyAdmins = () => {
    if (!canNotifyMilk) {
      toast.error("Carga la leche de un día antes de notificar.", { position: "top-center" });
      return;
    }
    setNotifyOpen(true);
  };

  const handleNotifyDay = async (date) => {
    if (!date) return;
    setNotifying(true);
    try {
      await pushAdminNotification({
        type: "milk",
        date,
        source: "web",
      });
      setNotifyOpen(false);
      toast.success("Se notificó a los administradores", { position: "top-center" });
      window.dispatchEvent(new Event("notifications:refresh"));
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo enviar la notificación", {
        position: "top-center",
      });
    } finally {
      setNotifying(false);
    }
  };

  const handleExportWeek = async () => {
    if (!weekly) {
      toast.error("No hay datos de la semana para exportar.", { position: "top-center" });
      return;
    }
    setWeekExporting(true);
    try {
      let bcv = null;
      try {
        const res = await getBcvRate();
        bcv = res.data?.data;
      } catch {
        bcv = null;
      }
      await exportMilkWeekExcel({ weekly, bcvRate: bcv, resumen: weekResumen });
      toast.success("Excel de la semana descargado", { position: "top-center" });
    } catch (err) {
      console.error(err);
      toast.error(err.message || "No se pudo exportar la semana.", { position: "top-center" });
    } finally {
      setWeekExporting(false);
    }
  };

  const runExport = async (format) => {
    if (!selectedRow) {
      toast.error("Selecciona un proveedor", { position: "top-center" });
      return;
    }
    setExportFormatOpen(false);
    setExporting(true);
    try {
      const snapshot = weekOpen ? weekSheetRef.current?.getExportSnapshot?.() : null;
      let bcv = null;
      try {
        const res = await getBcvRate();
        bcv = res.data?.data;
      } catch {
        bcv = null;
      }
      const payload = {
        provider: selectedRow,
        weekly,
        bcvRate: bcv,
        payments: weekPayments,
        daily: snapshot?.daily,
        prices: snapshot?.prices,
      };
      if (format === "image") {
        setReceiptCapture(buildMilkReceiptModel(payload));
        return;
      }
      await exportProviderMilkTemplate(payload);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "No se pudo exportar el recibo.", { position: "top-center" });
      setExporting(false);
    }
    if (format !== "image") setExporting(false);
  };

  useEffect(() => {
    if (!receiptCapture) return undefined;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        const waitForNode = async () => {
          if (receiptSheetRef.current) return receiptSheetRef.current;
          await new Promise((resolve) => setTimeout(resolve, 50));
          return receiptSheetRef.current;
        };
        const node = await waitForNode();
        if (cancelled || !node) {
          if (!cancelled) setExporting(false);
          return;
        }
        try {
          const result = await shareOrDownloadInvoiceImage(
            node,
            `${receiptCapture.filenameBase}.png`,
          );
          if (cancelled) return;
          if (result === "shared") {
            toast.success("Imagen lista para enviar");
          } else {
            toast.success("Imagen descargada");
          }
        } catch (err) {
          if (err?.name === "AbortError") return;
          console.error(err);
          if (!cancelled) {
            toast.error("No se pudo generar la imagen del recibo.", { position: "top-center" });
          }
        } finally {
          if (!cancelled) {
            setReceiptCapture(null);
            setExporting(false);
          }
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [receiptCapture]);

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("¿Eliminar este abono?")) return;
    try {
      await deleteMilkProviderPayment(paymentId);
      toast.success("Abono eliminado", { position: "top-center" });
      await fetchData({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "No se pudo eliminar el abono.", {
        position: "top-center",
      });
    }
  };

  const stopRowEvent = (event) => {
    event.stopPropagation();
  };

  const openActionMenu = (event, row) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 196;
    const menuHeight = hidePrices ? 52 : 220;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    let top = rect.bottom + 4;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - 4);
    }
    setActionMenu({ id: row.id, top, left, row });
  };

  useEffect(() => {
    if (!actionMenu) return undefined;
    const close = () => setActionMenu(null);
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    const onPointer = (event) => {
      if (actionMenuRef.current?.contains(event.target)) return;
      if (event.target.closest?.("[data-provider-actions]")) return;
      close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [actionMenu]);

  const runMenuAction = (action) => {
    const row = actionMenu?.row;
    setActionMenu(null);
    if (row) action(row);
  };

  const providerActions = (row) => (
    <button
      type="button"
      data-provider-actions
      aria-label={`Acciones de ${row.name || "proveedor"}`}
      title="Acciones"
      onClick={(event) => {
        stopRowEvent(event);
        if (actionMenu?.id === row.id) setActionMenu(null);
        else openActionMenu(event, row);
      }}
      onDoubleClick={stopRowEvent}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${
        actionMenu?.id === row.id
          ? "bg-white text-gray-900 shadow-sm"
          : "text-gray-600 hover:bg-white/80 hover:text-gray-900"
      }`}
    >
      <MoreHorizontal size={18} />
    </button>
  );

  const tableCols = (hidePrices ? 4 : 7) + 1;

  return (
    <div className="p-4 max-w-7xl mx-auto flex flex-col min-h-0 pb-24 md:pb-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <h1 className="text-xl font-semibold text-gray-900">Proveedores de leche</h1>
        <div className="flex flex-wrap items-center gap-2">
          {!hidePrices && (
            <Button
              onClick={() => {
                setEditingProvider(null);
                setProviderForm(emptyProviderForm());
                setShowProviderModal(true);
              }}
            >
              Nuevo
            </Button>
          )}
          <Button variant="secondary" onClick={() => fetchData()}>
            Actualizar
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-2 text-sm text-gray-500">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setWeekDate((d) => subWeeks(d, 1))}
          aria-label="Semana anterior"
        >
          {"<"}
        </Button>
        <span className="min-w-[260px]">{weekNavLabel(weekly)}</span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setWeekDate((d) => addWeeks(d, 1))}
          aria-label="Semana siguiente"
        >
          {">"}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setWeekDate(new Date())}>
          Hoy
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setWeekDate(new Date());
            setDayMilkPending(true);
          }}
          disabled={!weekly || rows.length === 0 || dayMilkPending}
        >
          {dayMilkPending ? "Cargando…" : "Leche de hoy"}
        </Button>
        {!hidePrices && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPricesOpen(true)}
            disabled={!weekly || rows.length === 0}
          >
            Ajustar precios
          </Button>
        )}
        {!hidePrices && (
          <Button variant="secondary" size="sm" onClick={handleExportWeek} disabled={weekExporting || !weekly}>
            {weekExporting ? "Exportando…" : "Excel semana"}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleNotifyAdmins}
          disabled={notifying || !canNotifyMilk}
          title={canNotifyMilk ? "Enviar la leche de un día a los administradores" : "Carga la leche de un día antes de notificar"}
        >
          {notifying ? "Notificando…" : "Notificar admins"}
        </Button>
        <span>Cierre: miércoles (jueves a miércoles)</span>
        <input
          ref={searchRef}
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSearchQuery("");
              e.currentTarget.blur();
            }
          }}
          placeholder="Buscar proveedor (Ctrl+F)"
          className="ml-auto min-w-[200px] flex-1 max-w-xs px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-800 bg-white outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30"
        />
      </div>

      <div className="mb-4">
        <MilkWeekResumenTable
          days={weekly?.days || []}
          dailyVaca={weekResumen.dailyVaca}
          dailyBufalo={weekResumen.dailyBufalo}
          dailyDiscount={weekResumen.dailyDiscount}
          litersVaca={weekResumen.litersVaca}
          litersBufalo={weekResumen.litersBufalo}
          discountLiters={weekResumen.discountLiters}
          priceCow={weekResumen.priceCow}
          priceBuffalo={weekResumen.priceBuffalo}
          owedVaca={weekResumen.owedVaca}
          owedBufalo={weekResumen.owedBufalo}
          discountAmount={weekResumen.discountAmount}
          hidePrices={hidePrices}
        />
        {!hidePrices && (
          <p className="text-sm text-gray-600 mt-2">
            Abonado: {fmtMoney(weekly?.totalPaid)}{"    "}
            Pendiente: {fmtMoney(weekly?.totalPending)}
          </p>
        )}
      </div>

      <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden min-h-0">
        <div className="overflow-auto max-h-[min(70vh,640px)]">
          <table className="w-full text-sm text-left hidden sm:table">
            <thead className="bg-[#e7efe9] text-gray-800 sticky top-0 z-20">
              <tr>
                <th className="px-4 py-2 font-semibold">Proveedor</th>
                <th className="px-4 py-2 font-semibold text-right">Vaca (L)</th>
                <th className="px-4 py-2 font-semibold text-right">Búfala (L)</th>
                <th className="px-4 py-2 font-semibold text-right">Desc. (L)</th>
                {!hidePrices && (
                  <>
                    <th className="px-4 py-2 font-semibold text-right">Total $</th>
                    <th className="px-4 py-2 font-semibold text-right">Abonado</th>
                    <th className="px-4 py-2 font-semibold text-right">Pendiente</th>
                  </>
                )}
                <th className="px-3 py-2 font-semibold text-center sticky right-0 bg-[#e7efe9] z-30 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.2)]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="reveal-rows">
              {loading && visibleRows.length === 0 ? (
                <SkeletonRows cols={tableCols} rows={6} />
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={tableCols} className="px-4 py-8 text-center text-gray-400">
                    {searchQuery.trim()
                      ? "Ningún proveedor coincide con la búsqueda."
                      : "No hay proveedores registrados."}
                  </td>
                </tr>
              ) : (
                visibleRows.map((p) => {
                  const selected = selectedId === p.id;
                  const payStatus = milkPayStatus(p);
                  const rowTone = hidePrices
                    ? selected
                      ? "bg-[#d4ebdd]"
                      : "hover:bg-gray-50"
                    : selected
                      ? "bg-[#d4ebdd]"
                      : payStatus === "paid"
                        ? "bg-green-50 hover:bg-green-100"
                        : payStatus === "pending"
                          ? "bg-yellow-50 hover:bg-yellow-100"
                          : "bg-red-50 hover:bg-red-100";
                  const actionBg = selected
                    ? "bg-[#d4ebdd]"
                    : hidePrices
                      ? "bg-white group-hover:bg-gray-50"
                      : payStatus === "paid"
                        ? "bg-green-50 group-hover:bg-green-100"
                        : payStatus === "pending"
                          ? "bg-yellow-50 group-hover:bg-yellow-100"
                          : "bg-red-50 group-hover:bg-red-100";
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setSelectedId(p.id)}
                      onDoubleClick={() => openWeek(p)}
                      className={`group border-t border-gray-100 cursor-pointer ${rowTone}`}
                    >
                      <td className="px-4 py-2 font-medium text-gray-900">{p.name}</td>
                      <td className="px-4 py-2 text-right">{fmtLiters(providerGrossLiters(p, "vaca"))}</td>
                      <td className="px-4 py-2 text-right">{fmtLiters(providerGrossLiters(p, "bufalo"))}</td>
                      <td className="px-4 py-2 text-right">{fmtLiters(providerDiscountLiters(p))}</td>
                      {!hidePrices && (
                        <>
                          <td className="px-4 py-2 text-right">{fmtMoney(p.totalOwed)}</td>
                          <td className="px-4 py-2 text-right">{fmtMoney(p.totalPaid)}</td>
                          <td
                            className={`px-4 py-2 text-right font-medium ${
                              payStatus === "paid"
                                ? "text-green-700"
                                : payStatus === "pending"
                                  ? "text-amber-700"
                                  : "text-red-700"
                            }`}
                          >
                            {fmtMoney(p.pendingAmount)}
                          </td>
                        </>
                      )}
                      <td
                        className={`px-3 py-2 text-center sticky right-0 z-10 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)] ${actionBg}`}
                      >
                        {providerActions(p)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <div className="sm:hidden divide-y divide-gray-100 reveal-host">
            {loading && visibleRows.length === 0 ? (
              <LoadingState count={5} label="Cargando proveedores" />
            ) : (
            visibleRows.map((p) => {
              const selected = selectedId === p.id;
              const payStatus = milkPayStatus(p);
              const rowTone = hidePrices
                ? selected
                  ? "bg-[#d4ebdd]"
                  : ""
                : selected
                  ? "bg-[#d4ebdd]"
                  : payStatus === "paid"
                    ? "bg-green-50"
                    : payStatus === "pending"
                      ? "bg-yellow-50"
                      : "bg-red-50";
              return (
                <div
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  onDoubleClick={() => openWeek(p)}
                  className={`w-full text-left p-3 ${rowTone}`}
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-gray-900">{p.name}</span>
                    {!hidePrices && (
                      <span
                        className={`text-sm font-medium ${
                          payStatus === "paid"
                            ? "text-green-700"
                            : payStatus === "pending"
                              ? "text-amber-700"
                              : "text-red-700"
                        }`}
                      >
                        {fmtMoney(p.pendingAmount)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Vaca {fmtLiters(providerGrossLiters(p, "vaca"))} L · Búfala{" "}
                    {fmtLiters(providerGrossLiters(p, "bufalo"))} L
                    {providerDiscountLiters(p) > 0
                      ? ` · Desc. ${fmtLiters(providerDiscountLiters(p))} L`
                      : ""}
                  </div>
                  {!hidePrices && (
                    <div className="text-xs text-gray-500">
                      Total {fmtMoney(p.totalOwed)} · Abonado {fmtMoney(p.totalPaid)}
                    </div>
                  )}
                  <div className="mt-2 flex justify-end">{providerActions(p)}</div>
                </div>
              );
            })
            )}
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-500 mt-2">
        {searchQuery.trim()
          ? `${visibleRows.length} de ${rows.length} proveedores · cierre los miércoles`
          : `${rows.length} proveedores · cierre los miércoles`}
      </p>

      <WeekPricesDialog
        open={!hidePrices && pricesOpen && Boolean(weekly)}
        onClose={() => setPricesOpen(false)}
        providers={rows}
        weekStart={weekly?.startOfWeek}
        weekEnd={weekly?.endOfWeek}
        weekLabel={weekNavLabel(weekly)}
        onSaved={() => fetchData({ silent: true })}
      />

      <QuickDayMilkDialog
        open={dayMilkOpen && Boolean(weekly)}
        onClose={() => setDayMilkOpen(false)}
        providers={rows}
        days={weekly?.days || []}
        initialDate={format(new Date(), "yyyy-MM-dd")}
        onSaved={async (savedDate) => {
          if (savedDate) setWeekDate(new Date(`${savedDate}T12:00:00`));
          await fetchData({ silent: true });
        }}
      />

      <AppModalShell
        open={weekOpen && Boolean(selectedRow)}
        onClose={async () => {
          await weekSheetRef.current?.save?.({ source: "auto" });
          setWeekOpen(false);
        }}
        title={`Semana — ${selectedRow?.name || ""}`}
        widthClass="max-w-5xl w-full"
        footer={
          <AppModalFooter>
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={async () => {
                await weekSheetRef.current?.save?.({ source: "auto" });
                setWeekOpen(false);
              }}
            >
              Cerrar
            </Button>
            {!hidePrices && (
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => handleExport()}
                disabled={exporting}
              >
                {exporting ? "Exportando…" : "Exportar"}
              </Button>
            )}
            <Button
              className="w-full sm:w-auto"
              onClick={() => weekSheetRef.current?.save()}
              disabled={weekSaving}
            >
              {weekSaving ? "Guardando…" : "Guardar"}
            </Button>
          </AppModalFooter>
        }
      >
        {selectedRow && weekly && (
          <WeeklyMilkSheet
            ref={weekSheetRef}
            key={`${selectedRow.id}-${weekly.days?.[0]?.date || ""}`}
            days={weekly.days || []}
            provider={selectedRow}
            weekPayments={weekPayments}
            onSaveWeek={handleSaveWeek}
            onSavingChange={setWeekSaving}
            onRegisterPayment={() => setShowPaymentModal(true)}
            onDeletePayment={handleDeletePayment}
            hidePrices={hidePrices}
          />
        )}
      </AppModalShell>

      <AppModalShell
        open={showPaymentModal && Boolean(selectedRow)}
        onClose={() => setShowPaymentModal(false)}
        title={`Abono — ${selectedRow?.name || ""}`}
        widthClass="max-w-md w-full"
        fitContent
        zClass="z-[80]"
      >
        {selectedRow && weekly && (
          <MilkProviderPaymentForm
            provider={selectedRow}
            weekStart={weekly.days?.[0]?.date || dateKey(weekly.startOfWeek)}
            weekEnd={weekly.days?.[6]?.date || dateKey(weekly.endOfWeek)}
            weekPayments={[]}
            pendingAmount={selectedRow.pendingAmount || 0}
            onSuccess={() => {
              fetchData();
              setShowPaymentModal(false);
            }}
          />
        )}
      </AppModalShell>

      <AppModalShell
        open={showProviderModal}
        onClose={() => {
          setShowProviderModal(false);
          setEditingProvider(null);
          setProviderForm(emptyProviderForm());
        }}
        title={editingProvider ? "Editar proveedor" : "Nuevo proveedor"}
        widthClass="max-w-md w-full"
        fitContent
        zClass="z-[80]"
        footer={
          <AppModalFooter>
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => {
                setShowProviderModal(false);
                setEditingProvider(null);
                setProviderForm(emptyProviderForm());
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" form="provider-form" className="w-full sm:w-auto">
              Guardar
            </Button>
          </AppModalFooter>
        }
      >
        <form id="provider-form" onSubmit={handleCreateProvider} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Nombre *</label>
            <input
              type="text"
              required
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
              value={providerForm.name}
              onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Documento</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
              value={providerForm.documentId}
              onChange={(e) => setProviderForm({ ...providerForm, documentId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Teléfono</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-emerald-600"
              value={providerForm.phone}
              onChange={(e) => setProviderForm({ ...providerForm, phone: e.target.value })}
            />
          </div>
        </form>
      </AppModalShell>

      <AppModalShell
        open={exportFormatOpen}
        onClose={() => setExportFormatOpen(false)}
        title="Exportar recibo"
        widthClass="max-w-md"
        zClass="z-[80]"
        footer={
          <AppModalFooter>
            <span />
            <Button variant="ghost" className="w-full sm:w-auto" onClick={() => setExportFormatOpen(false)}>
              Cancelar
            </Button>
          </AppModalFooter>
        }
      >
        <p className="text-sm text-gray-500 mb-4">¿Cómo deseas exportar el recibo de la plantilla?</p>
        <div className="space-y-2">
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => runExport("excel")}
            disabled={exporting}
          >
            Excel
          </Button>
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => runExport("image")}
            disabled={exporting}
          >
            Imagen
          </Button>
        </div>
      </AppModalShell>

      <NotifyDayDialog
        open={notifyOpen}
        title="Notificar leche del día"
        hint="Selecciona el día que ya tiene leche cargada."
        days={notifyDays}
        confirming={notifying}
        onClose={() => setNotifyOpen(false)}
        onConfirm={handleNotifyDay}
      />

      {actionMenu && (
        <div
          ref={actionMenuRef}
          role="menu"
          className="fixed z-[90] w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          style={{ top: actionMenu.top, left: actionMenu.left }}
        >
          {!hidePrices && (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
              onClick={() => runMenuAction(handleEdit)}
            >
              <Pencil size={16} className="text-gray-500" />
              Editar
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
            onClick={() => runMenuAction(openWeek)}
          >
            <CalendarRange size={16} className="text-gray-500" />
            Ver semana
          </button>
          {!hidePrices && (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
              onClick={() => runMenuAction(openPayment)}
            >
              <Banknote size={16} className="text-gray-500" />
              Abonar
            </button>
          )}
          {!hidePrices && (
            <button
              type="button"
              role="menuitem"
              disabled={exporting}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => runMenuAction(handleExport)}
            >
              <Download size={16} className="text-gray-500" />
              {exporting && selectedId === actionMenu.id ? "Exportando…" : "Exportar"}
            </button>
          )}
          {!hidePrices && (
            <button
              type="button"
              role="menuitem"
              disabled={Boolean(deletingProviderId)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
              onClick={() => runMenuAction(handleDelete)}
            >
              <Trash2 size={16} />
              {deletingProviderId === actionMenu.id ? "Eliminando…" : "Eliminar"}
            </button>
          )}
        </div>
      )}

      {receiptCapture ? (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: -10000,
            top: 0,
            pointerEvents: "none",
          }}
        >
          <MilkReceiptSheet ref={receiptSheetRef} model={receiptCapture} />
        </div>
      ) : null}
    </div>
  );
};

export default ProvidersModule;
